import { Sort } from '@compass/client/types/common';
import {
  CallDirection,
  type Evaluation,
  EvaluationStatus,
  type GetEvaluationCountsRequest,
  type GetEvaluationCountsResponse,
  type ListEvaluationsRequest,
  type SectionScoreSort,
} from '@compass/client/types/evaluation';
import { evaluationService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { SupportedTimeZone } from '@/constants/date';
import { QC_LIST_QUERY_CONFIG } from '@/constants/queryConfig';
import { naiveDateToUtc } from '@/utils/date';
import { isValidObjectId } from '@/utils/validation';

import {
  type CallIdSearchInput,
  parseCallIdSearchInput,
  type QcQuickFilter,
  type QcScoreBand,
  SCORE_BAND_EXEMPLARY_MIN,
  SCORE_BAND_MEETS_MIN,
} from './utils';

type ValidCallIdSearchInput = Extract<CallIdSearchInput, { kind: 'valid' }>;

export interface QcEvaluationFilters {
  organizationId?: string | null;
  programIds?: string[];
  callId?: string;
  evaluationId?: string;
  agentIds?: string[];
  callDirections?: CallDirection[];
  scoreBand?: QcScoreBand;
  dateFrom?: string;
  dateTo?: string;
  quickFilter?: QcQuickFilter;
  sortByCallStartTime?: Sort;
  sortByAggregateScore?: Sort;
  // AQ-649: below aggregate_score, above call_start_time on the backend.
  sortByCallDuration?: Sort;
  // AQ-800: takes precedence over the other sorts on the backend.
  sortBySectionScore?: SectionScoreSort;
  pageSize: number;
  startAfterId?: string;
  timeZone: SupportedTimeZone;
  // Metadata filters keyed by (slugified) metadata key -> value, applied as
  // metadata.<key>=<value> on the ListEvaluations request. Populated from
  // `metadata.*` URL params (e.g. analytics breakdown drill-down).
  metadata?: Record<string, string>;
  // AQ-653: "Show only ineligible evaluations" — when true, the list and tab
  // counts are narrowed to ineligible evaluations only.
  onlyIneligible?: boolean;
  // AQ-499: "Show only reviewed evaluations" — list and tab counts narrowed to
  // evaluations with a human review. Served from ClickHouse only.
  onlyReviewed?: boolean;
}

const SCORE_BAND_UPPER_BOUND_EPSILON = 0.000001;
const EMPTY_EVALUATIONS_RESULT = {
  evaluations: [] as Evaluation[],
  nextCursor: undefined as string | undefined,
};
const EMPTY_COUNTS_RESULT: GetEvaluationCountsResponse = {
  total_count: 0,
  groups: [],
};
const DEFAULT_TAB_COUNTS = {
  all: 0,
  needsReview: 0,
  escalations: 0,
  complianceFailure: 0,
};

const normalizeIds = (values?: string[]) => [...new Set(values?.filter(Boolean) ?? [])].sort();
const normalizeCallDirections = (values?: CallDirection[]) =>
  [...new Set(values ?? [])].filter(
    value =>
      value === CallDirection.CALL_DIRECTION_INBOUND ||
      value === CallDirection.CALL_DIRECTION_OUTBOUND,
  );

const getScoreBandRange = (scoreBand: QcScoreBand = 'all') => {
  switch (scoreBand) {
    case 'exemplary':
      return { aggregate_score_gte: SCORE_BAND_EXEMPLARY_MIN };
    case 'meets':
      return {
        aggregate_score_gte: SCORE_BAND_MEETS_MIN,
        aggregate_score_lte: SCORE_BAND_EXEMPLARY_MIN - SCORE_BAND_UPPER_BOUND_EPSILON,
      };
    case 'needs_coaching':
      return {
        aggregate_score_lte: SCORE_BAND_MEETS_MIN - SCORE_BAND_UPPER_BOUND_EPSILON,
      };
    default:
      return {};
  }
};

// Metadata filters must be sent as FLAT dot-notation keys on the request object
// (request["metadata.<key>"] = value), NOT a nested `metadata: {}` object: the
// axios paramsSerializer would serialize a nested object as metadata[key]=…, and
// the React Query cache-key builder (extractDotKeys) also expects flat keys. The
// backend reads HTTP dot-notation metadata.key=value.
const getMetadataParams = (metadata?: Record<string, string>): Record<string, string> => {
  if (!metadata) return {};
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (key) params[`metadata.${key}`] = value;
  }
  return params;
};

const getQuickFilterParams = (quickFilter: QcQuickFilter = 'all'): Record<string, unknown> => {
  switch (quickFilter) {
    case 'needs_review':
      return { needs_review: true };
    case 'escalations':
      return { flag_escalation: true };
    case 'compliance_failure':
      return { flag_compliance_failed: true };
    default:
      return {};
  }
};

const getCallStartTimeRange = (filters: QcEvaluationFilters) => ({
  // QC date filtering is based on the call's real start time.
  call_start_time_after: filters.dateFrom
    ? naiveDateToUtc(filters.dateFrom, filters.timeZone, 'start')
    : undefined,
  call_start_time_before: filters.dateTo
    ? naiveDateToUtc(filters.dateTo, filters.timeZone, 'end')
    : undefined,
});

const buildCallIdSearchFilters = (
  filters: QcEvaluationFilters,
  callIdSearch: ValidCallIdSearchInput,
) => ({
  organization_id: filters.organizationId || undefined,
  call_ids: callIdSearch.values,
  statuses: [EvaluationStatus.EVALUATION_STATUS_COMPLETED],
  only_ineligible: filters.onlyIneligible || undefined,
  only_reviewed: filters.onlyReviewed || undefined,
});

const buildRequestFilters = (
  filters: QcEvaluationFilters,
  callIdSearch: CallIdSearchInput,
  quickFilter: QcQuickFilter = filters.quickFilter ?? 'all',
) => {
  if (callIdSearch.kind === 'valid') {
    return buildCallIdSearchFilters(filters, callIdSearch);
  }

  const normalizedProgramIds = normalizeIds(filters.programIds);
  const normalizedAgentIds = normalizeIds(filters.agentIds);
  const normalizedCallDirections = normalizeCallDirections(filters.callDirections);

  return {
    organization_id: filters.organizationId || undefined,
    program_ids: normalizedProgramIds.length > 0 ? normalizedProgramIds : undefined,
    agent_user_ids: normalizedAgentIds.length > 0 ? normalizedAgentIds : undefined,
    call_directions: normalizedCallDirections.length > 0 ? normalizedCallDirections : undefined,
    statuses: [EvaluationStatus.EVALUATION_STATUS_COMPLETED],
    only_ineligible: filters.onlyIneligible || undefined,
    only_reviewed: filters.onlyReviewed || undefined,
    ...getScoreBandRange(filters.scoreBand),
    ...getCallStartTimeRange(filters),
    ...getQuickFilterParams(quickFilter),
    ...getMetadataParams(filters.metadata),
  };
};

/**
 * Builds a ListEvaluationsRequest (without pagination) for QC export.
 * Returns undefined if the filters are not valid for a request.
 */
export const buildQcExportFilters = (
  filters: QcEvaluationFilters,
): Omit<ListEvaluationsRequest, 'limit' | 'start_after_id'> | undefined => {
  if (!filters.organizationId) return undefined;

  const callIdSearch = parseCallIdSearchInput(filters.callId);
  if (callIdSearch.kind === 'invalid') return undefined;

  // TODO(AQ-499): export ignores "Show only reviewed" — reviewed lives only in ClickHouse
  // evaluation_list, and export needs full Mongo records. Needs CH → evaluation IDs →
  // Mongo hydrate (new evaluation_ids filter on ListEvaluationsRequest).
  return {
    ...buildRequestFilters(filters, callIdSearch, filters.quickFilter ?? 'all'),
    only_reviewed: undefined,
  };
};

const buildCountFilters = (
  filters: QcEvaluationFilters,
  callIdSearch: CallIdSearchInput,
): GetEvaluationCountsRequest => {
  if (callIdSearch.kind === 'valid') {
    return buildCallIdSearchFilters(filters, callIdSearch) as GetEvaluationCountsRequest;
  }

  const normalizedProgramIds = normalizeIds(filters.programIds);
  const normalizedAgentIds = normalizeIds(filters.agentIds);
  const normalizedCallDirections = normalizeCallDirections(filters.callDirections);

  return {
    organization_id: filters.organizationId || undefined,
    program_ids: normalizedProgramIds.length > 0 ? normalizedProgramIds : undefined,
    agent_user_ids: normalizedAgentIds.length > 0 ? normalizedAgentIds : undefined,
    call_directions: normalizedCallDirections.length > 0 ? normalizedCallDirections : undefined,
    statuses: [EvaluationStatus.EVALUATION_STATUS_COMPLETED],
    only_ineligible: filters.onlyIneligible || undefined,
    only_reviewed: filters.onlyReviewed || undefined,
    ...getScoreBandRange(filters.scoreBand),
    ...getCallStartTimeRange(filters),
    ...getMetadataParams(filters.metadata),
  };
};

// Only an explicit direction is sent; SORT_NONE / unset leaves the param off.
const explicitSort = (sort?: Sort) =>
  sort === Sort.SORT_ASC || sort === Sort.SORT_DESC ? sort : undefined;

const buildBaseRequest = (
  filters: QcEvaluationFilters,
  callIdSearch: CallIdSearchInput,
  overrides: {
    limit?: number;
    startAfterId?: string;
    quickFilter?: QcQuickFilter;
  } = {},
): ListEvaluationsRequest => ({
  ...buildRequestFilters(
    filters,
    callIdSearch,
    overrides.quickFilter ?? filters.quickFilter ?? 'all',
  ),
  limit: overrides.limit ?? filters.pageSize,
  start_after_id: overrides.startAfterId ?? filters.startAfterId,
  sort_by_call_start_time: explicitSort(filters.sortByCallStartTime),
  sort_by_aggregate_score: explicitSort(filters.sortByAggregateScore),
  sort_by_call_duration: explicitSort(filters.sortByCallDuration),
  sort_by_section_score: filters.sortBySectionScore,
});

const buildCountsRequest = (
  filters: QcEvaluationFilters,
  callIdSearch: CallIdSearchInput,
): GetEvaluationCountsRequest => buildCountFilters(filters, callIdSearch);

const buildListRequest = (
  filters: QcEvaluationFilters,
  callIdSearch: CallIdSearchInput,
  activeQuickFilter: QcQuickFilter,
) =>
  buildBaseRequest(
    {
      ...filters,
      quickFilter: activeQuickFilter,
    },
    callIdSearch,
    {
      quickFilter: activeQuickFilter,
    },
  );

// Extracts dot-notation flat keys with the given prefix from a request object.
// Used to include metadata.* / metadata_counts.* filters in React Query cache keys
// so they participate in cache invalidation.
const extractDotKeys = (
  request: Record<string, unknown>,
  prefix: string,
): { [key: string]: string } | undefined => {
  const result: { [key: string]: string } = {};
  for (const [key, value] of Object.entries(request)) {
    if (key.startsWith(prefix) && typeof value === 'string') {
      result[key] = value;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const buildEvaluationsQueryKey = (
  organizationId: string,
  request: ListEvaluationsRequest,
  mode: string,
) =>
  queryKeys.evaluations.byOrganization(organizationId, {
    program_ids: request.program_ids,
    call_ids: request.call_ids,
    agent_user_ids: request.agent_user_ids,
    call_directions: request.call_directions,
    statuses: request.statuses,
    aggregate_score_gte: request.aggregate_score_gte,
    aggregate_score_lte: request.aggregate_score_lte,
    call_start_time_after: request.call_start_time_after,
    call_start_time_before: request.call_start_time_before,
    evaluated_at_after: request.evaluated_at_after,
    evaluated_at_before: request.evaluated_at_before,
    flag_escalation: request.flag_escalation,
    flag_compliance_failed: request.flag_compliance_failed,
    needs_review: request.needs_review,
    only_ineligible: request.only_ineligible,
    only_reviewed: request.only_reviewed,
    sort_by_call_start_time: request.sort_by_call_start_time,
    sort_by_aggregate_score: request.sort_by_aggregate_score,
    sort_by_call_duration: request.sort_by_call_duration,
    sort_by_section_score: request.sort_by_section_score,
    metadata: extractDotKeys(request as Record<string, unknown>, 'metadata.'),
    limit: request.limit,
    start_after_id: request.start_after_id,
    mode,
  });

const buildFallbackQueryKey = (mode: string) => queryKeys.evaluations.byOrganization('', { mode });

export const buildCountsQueryKey = (
  organizationId: string | undefined,
  request?: GetEvaluationCountsRequest,
) =>
  request
    ? queryKeys.evaluations.countsByOrganization(organizationId || '', {
        program_ids: request.program_ids,
        call_ids: request.call_ids,
        agent_user_ids: request.agent_user_ids,
        call_directions: request.call_directions,
        aggregate_score_gte: request.aggregate_score_gte,
        aggregate_score_lte: request.aggregate_score_lte,
        call_start_time_after: request.call_start_time_after,
        call_start_time_before: request.call_start_time_before,
        only_ineligible: request.only_ineligible,
        only_reviewed: request.only_reviewed,
        // Base metadata filter (metadata.<key>) — must be in the key so the counts
        // query refetches when the metadata filter changes. This was previously
        // extracting the wrong prefix (metadata_counts.), so metadata-filtered
        // navigation never re-ran GetEvaluationCounts and tab counts went stale.
        metadata: extractDotKeys(request as unknown as Record<string, unknown>, 'metadata.'),
        // Facet-count groups (metadata_counts.<key>), a separate feature — keep in
        // the key so it also invalidates independently.
        metadata_counts: extractDotKeys(
          request as unknown as Record<string, unknown>,
          'metadata_counts.',
        ),
      })
    : queryKeys.evaluations.countsByOrganization('', undefined);

const buildListQueryKey = (
  organizationId: string | undefined,
  request: ListEvaluationsRequest | undefined,
) =>
  request
    ? buildEvaluationsQueryKey(organizationId || '', request, 'qc_list')
    : buildFallbackQueryKey('qc_list');

// The backend reads the nested SectionScoreSort as flat dot-notation query params, like
// metadata.<key> (see getMetadataParams): axios would send the object as
// sort_by_section_score[section_id]=…. Flattened here, at the call, so the request and its
// query key stay typed; the generated evaluation service is overwritten by `pnpm gen`.
const toListEvaluationsParams = ({
  sort_by_section_score: sectionScoreSort,
  ...request
}: ListEvaluationsRequest): ListEvaluationsRequest =>
  sectionScoreSort
    ? ({
        ...request,
        'sort_by_section_score.section_id': sectionScoreSort.section_id,
        'sort_by_section_score.sort': sectionScoreSort.sort,
      } as ListEvaluationsRequest)
    : request;

const fetchEvaluations = async (request: ListEvaluationsRequest | undefined) => {
  if (!request) {
    return EMPTY_EVALUATIONS_RESULT;
  }

  const response = await evaluationService.listEvaluations(toListEvaluationsParams(request));
  return {
    evaluations: response.evaluations ?? [],
    nextCursor: response.next_cursor || undefined,
  };
};

const fetchEvaluationCounts = async (request: GetEvaluationCountsRequest | undefined) => {
  if (!request) {
    return EMPTY_COUNTS_RESULT;
  }

  return evaluationService.getEvaluationCounts(request);
};

const buildTabCounts = (response?: GetEvaluationCountsResponse) => {
  if (!response) {
    return DEFAULT_TAB_COUNTS;
  }

  const groupCounts = new Map(
    (response.groups ?? []).map(groupCount => [groupCount.group, groupCount.count ?? 0]),
  );

  return {
    all: response.total_count ?? 0,
    needsReview: groupCounts.get('needs_review') ?? 0,
    escalations: groupCounts.get('escalations') ?? 0,
    complianceFailure: groupCounts.get('compliance_failure') ?? 0,
  };
};

const buildQcRequests = ({
  filters,
  organizationId,
  activeQuickFilter,
  callIdSearch,
}: {
  filters: QcEvaluationFilters;
  organizationId?: string;
  activeQuickFilter: QcQuickFilter;
  callIdSearch: CallIdSearchInput;
}) => {
  if (!organizationId || callIdSearch.kind === 'invalid') {
    return {
      countsRequest: undefined,
      listRequest: undefined,
    };
  }

  return {
    countsRequest: buildCountsRequest(filters, callIdSearch),
    listRequest: buildListRequest(filters, callIdSearch, activeQuickFilter),
  };
};

type EvaluationIdSearchInput =
  | { kind: 'none' }
  | { kind: 'valid'; value: string }
  | { kind: 'invalid' };

const parseEvaluationIdSearchInput = (value?: string): EvaluationIdSearchInput => {
  const trimmed = value?.trim();
  if (!trimmed) return { kind: 'none' };
  if (isValidObjectId(trimmed)) return { kind: 'valid', value: trimmed };
  return { kind: 'invalid' };
};

const buildEvaluationIdTabCounts = (evaluation: Evaluation | null | undefined) => {
  if (!evaluation) return DEFAULT_TAB_COUNTS;
  return {
    all: 1,
    needsReview: evaluation.needs_review ? 1 : 0,
    escalations: evaluation.flags?.escalation ? 1 : 0,
    complianceFailure: evaluation.flags?.compliance_failed ? 1 : 0,
  };
};

const useEvaluationByIdSearch = (
  evaluationIdSearch: EvaluationIdSearchInput,
  organizationId: string | undefined,
) => {
  const evaluationIdValue = evaluationIdSearch.kind === 'valid' ? evaluationIdSearch.value : '';

  return useQuery({
    queryKey: [
      ...queryKeys.evaluations.byId(evaluationIdValue),
      organizationId,
      'qc_search',
    ] as const,
    queryFn: async () => {
      if (evaluationIdSearch.kind !== 'valid' || !organizationId) return null;
      try {
        const response = await evaluationService.getEvaluation({
          organization_id: organizationId,
          evaluation_id: evaluationIdSearch.value,
          // AQ-653: resolve ineligible evaluations too, so an ID search (like the
          // detail page) can find them — GetEvaluation excludes ineligible by default.
          ineligible: true,
        });
        return response.evaluation ?? null;
      } catch (err) {
        if ((err as { APIHttpStatus?: number })?.APIHttpStatus === 404) return null;
        throw err;
      }
    },
    enabled: evaluationIdSearch.kind === 'valid' && !!organizationId,
    ...QC_LIST_QUERY_CONFIG,
  });
};

const EMPTY_QC_RESULT = {
  evaluations: [] as Evaluation[],
  error: undefined,
  isLoading: false,
  hasNextPage: false,
  lastEvaluationId: undefined,
  tabCounts: DEFAULT_TAB_COUNTS,
  tabCountsLoading: false,
};

const useQcListQueries = (filters: QcEvaluationFilters) => {
  const organizationId = filters.organizationId || undefined;
  const activeQuickFilter = filters.quickFilter ?? 'all';
  const callIdSearch = useMemo(() => parseCallIdSearchInput(filters.callId), [filters.callId]);
  const evaluationIdSearch = useMemo(
    () => parseEvaluationIdSearchInput(filters.evaluationId),
    [filters.evaluationId],
  );
  const isSearchingByEvaluationId = evaluationIdSearch.kind === 'valid';

  const { countsRequest, listRequest } = useMemo(
    () =>
      buildQcRequests({
        filters,
        organizationId,
        activeQuickFilter,
        callIdSearch,
      }),
    [activeQuickFilter, callIdSearch, filters, organizationId],
  );

  const countsQuery = useQuery({
    queryKey: buildCountsQueryKey(organizationId, countsRequest),
    queryFn: () => fetchEvaluationCounts(countsRequest),
    placeholderData: previousData => previousData,
    enabled: !!countsRequest && !isSearchingByEvaluationId,
    ...QC_LIST_QUERY_CONFIG,
  });

  const listQuery = useQuery({
    queryKey: buildListQueryKey(organizationId, listRequest),
    queryFn: () => fetchEvaluations(listRequest),
    enabled: !!listRequest && !isSearchingByEvaluationId,
    ...QC_LIST_QUERY_CONFIG,
  });

  const evaluationByIdQuery = useEvaluationByIdSearch(evaluationIdSearch, organizationId);

  return {
    callIdSearch,
    evaluationIdSearch,
    isSearchingByEvaluationId,
    countsQuery,
    listQuery,
    evaluationByIdQuery,
  };
};

const buildEvaluationIdResult = (query: ReturnType<typeof useEvaluationByIdSearch>) => {
  const evaluation = query.data;
  return {
    evaluations: evaluation ? [evaluation] : ([] as Evaluation[]),
    error: query.error,
    isLoading: query.isLoading,
    hasNextPage: false,
    lastEvaluationId: undefined,
    tabCounts: buildEvaluationIdTabCounts(evaluation),
    tabCountsLoading: false,
  };
};

const buildListResult = (
  listQuery: ReturnType<typeof useQuery<{ evaluations: Evaluation[]; nextCursor?: string } | null>>,
  countsQuery: ReturnType<typeof useQuery<GetEvaluationCountsResponse | null>>,
  tabCounts: ReturnType<typeof buildTabCounts>,
) => ({
  evaluations: (listQuery.data as { evaluations: Evaluation[] } | undefined)?.evaluations ?? [],
  error: listQuery.error ?? countsQuery.error,
  isLoading: listQuery.isLoading,
  hasNextPage: !!(listQuery.data as { nextCursor?: string } | undefined)?.nextCursor,
  lastEvaluationId: (listQuery.data as { nextCursor?: string } | undefined)?.nextCursor,
  tabCounts,
  tabCountsLoading: countsQuery.isFetching,
});

export const useQcPageData = (filters: QcEvaluationFilters) => {
  const {
    callIdSearch,
    evaluationIdSearch,
    isSearchingByEvaluationId,
    countsQuery,
    listQuery,
    evaluationByIdQuery,
  } = useQcListQueries(filters);

  const tabCounts = useMemo(() => buildTabCounts(countsQuery.data), [countsQuery.data]);

  if (callIdSearch.kind === 'invalid' || evaluationIdSearch.kind === 'invalid') {
    return EMPTY_QC_RESULT;
  }

  if (isSearchingByEvaluationId) {
    return buildEvaluationIdResult(evaluationByIdQuery);
  }

  return buildListResult(listQuery, countsQuery, tabCounts);
};
